import type { FileRecord, FileService } from "@/shared/domain/files";
import {
  type MessagePathRepresentationMapping,
  RepresentationRecord,
} from "@/shared/domain/topics";
import {
  CacheReadError,
  isAbortError,
  isCacheReadError,
} from "@/shared/errors";
import { LocalFile, RemoteFile } from "@/shared/fs";
import { LoggerService } from "@/shared/services/LoggerService";

import { type ReaderLike, McapReader, zipMessages } from "./mcap";
import type { Message } from "./Message";
import {
  type TopicReader,
  type IterLogTimeBatchesOptions,
  type IterMessagesOptions,
  type LogTimeIndexEntry,
  TopicReaderEventTarget,
} from "./TopicReader";

/**
 * A reader over one representation, paired with the OPFS entry it reads from, so a
 * failure can be attributed to the entry that produced it.
 */
interface CachedRepresentationReader {
  reader: ReaderLike;
  file: LocalFile;
}

// prettier-ignore
export class McapTopicReader extends TopicReaderEventTarget implements TopicReader {
  public readonly topicId: string;

  private cachedFileToReaderMap: Map<string, McapReader>;
  private fileService: FileService;
  private messagePathToRepresentationMaps: MessagePathRepresentationMapping[];
  private localFileCache: Map<string, LocalFile>;
  private representationFileRecordCache: Map<string, FileRecord>;

  constructor(
    topicId: string,
    messagePathToRepresentationMaps: MessagePathRepresentationMapping[],
    fileService: FileService,
  ) {
    super();
    this.topicId = topicId;

    this.cachedFileToReaderMap = new Map();
    this.fileService = fileService;
    this.messagePathToRepresentationMaps = messagePathToRepresentationMaps;
    this.localFileCache = new Map();
    this.representationFileRecordCache = new Map();
  }

  dispose(): void {
    this.removeAllListeners();
    this.cachedFileToReaderMap.clear();
    this.localFileCache.clear();
    this.representationFileRecordCache.clear();
  }

  async *iterLogTimeBatches({
    filter,
    signal,
  }: IterLogTimeBatchesOptions): AsyncGenerator<LogTimeIndexEntry[], void, undefined> {
    // GM(2025-05):
    // Heuristic: a topic has a single time index, which means that all representations will have the same log times.
    // Extract log time index from the first representation.
    // In many cases, topics stored in MCAP files will have all MessagePaths in the same MCAP file.
    // In those cases, the repr file downloaded and used here will be the same as the one used to iter messages.
    // Topics which use per-MessagePath MCAP files, however, will incur inefficiency in this implementation.
    // It would be better to identify a repr that holds MessagePath data that we'll need as part of iterMessages.
    const anyMessagePathId = this.messagePathToRepresentationMaps
      .at(0)
      ?.message_paths.at(0)?.message_path_id;
    if (anyMessagePathId === undefined) {
      throw new Error(`Failed to read data topic ${this.topicId}`, {
        cause:
          "This Topic does not have any mappings between MessagePaths and Representations",
      });
    }
    const cached = await this.collectReadersForMessagePaths(
      new Set([anyMessagePathId]),
      signal,
    );
    const { reader, file } = cached[0];
    yield* this.readFromCache(
      reader.iterMessageChunksIndices({ filter, abortSignal: signal }),
      signal,
      file,
    );
  }

  async *iterMessages({
    filter,
    messagePathsInclude,
    signal,
  }: IterMessagesOptions): AsyncGenerator<Message, void, undefined> {
    const cached = await this.collectReadersForMessagePaths(
      new Set(messagePathsInclude),
      signal,
    );
    // Each reader is scoped to its own cache entry before zipping, so a failure
    // evicts the representation that produced it rather than every representation
    // this reader holds. `zipMessages` reports a failure without saying which of
    // the readers it was pulling from raised it, so attributing after the fact is
    // not possible; attributing at the source is.
    yield* this.readFromCache(
      zipMessages(cached.map(this.scopeEvictionToReader), { filter, signal }),
      signal,
    );
  }

  /**
   * Returns the LocalFile handle for the OPFS-cached copy of a representation's MCAP file,
   * creating it on first access and memoizing it per representation. The on-disk path is:
   *
   *   $OPFSROOT / <topic_id> / <representation_id> / <associated_file_id>.<storage_format>
   *
   * The returned handle does not guarantee the bytes exist on disk; callers must check
   * with `isCached` before reading.
   */
  private cacheFileForRepresentation(
    representation: RepresentationRecord,
  ): LocalFile {
    const reprCacheKey = representation.association.association_id;
    if (this.localFileCache.has(reprCacheKey)) {
      return this.localFileCache.get(reprCacheKey)!;
    }
    const file = new LocalFile({
      parents: [this.topicId, representation.representation_id],
      name: `${representation.association.association_id}.${representation.storage_format.toLowerCase()}`,
    });
    this.localFileCache.set(reprCacheKey, file);
    return file;
  }

  private async collectReadersForMessagePaths(
    messagePathIds: Set<string>,
    signal?: AbortSignal,
  ): Promise<CachedRepresentationReader[]> {
    // An empty request is a no-op: the caller asked for zero message paths,
    // so zero readers is the correct answer. This is different from passing
    // message path ids that don't map to any representation, which is treated
    // as a configuration error and throws below (see the readers.length === 0
    // and remaining.size > 0 checks at the end of this method).
    if (messagePathIds.size === 0) {
      return [];
    }

    const remaining = new Set(messagePathIds);
    const readers: CachedRepresentationReader[] = [];
    for (const mapping of this.messagePathToRepresentationMaps) {
      const relevant = mapping.message_paths.filter((record) =>
        messagePathIds.has(record.message_path_id),
      );
      if (relevant.length === 0) {
        continue;
      }

      const representationFileRecord =
        await this.getRepresentationAssociatedFileRecord(
          mapping.representation,
          signal,
        );

      const local = this.cacheFileForRepresentation(mapping.representation);
      const cacheKey = local.id();
      // Serialize cache checks and downloads for this representation across every
      // McapTopicReader instance in the tab. Without this lock, two readers asked for
      // the same topic at once would both see "not cached" and race to download the
      // same MCAP file into OPFS.
      await navigator.locks.request(
        `collectReadersForMessagePaths#${cacheKey}`,
        { signal },
        async () => {
          signal?.throwIfAborted();

          const isCached = await this.isCached(
            local,
            representationFileRecord.size,
          );
          if (this.cachedFileToReaderMap.has(cacheKey)) {
            if (!isCached) {
              // OPFS evicted the bytes (e.g. storage pressure) since the reader was
              // built. Drop the stale reader so the fetch path below rebuilds it.
              this.cachedFileToReaderMap.delete(cacheKey);
            } else {
              const reader = this.cachedFileToReaderMap.get(cacheKey)!;
              relevant.forEach((mp) => {
                remaining.delete(mp.message_path_id);
              });
              readers.push({ reader, file: local });
              return;
            }
          }

          if (!isCached) {
            // Bytes are not in OPFS — fetch from S3. The downloadstart/downloadend
            // pair lets UI components show a loading indicator for this representation;
            // `priority: "foreground"` flags it as user-visible work (the user is
            // actively waiting on this topic's data).
            const loadingId = Symbol.for(cacheKey);
            this.dispatchEvent(
              new CustomEvent("downloadstart", {
                detail: { id: loadingId, priority: "foreground" },
              }),
            );
            const remote = new RemoteFile(
              representationFileRecord,
              this.fileService,
            );
            await remote.toLocalFile({
              path: local,
              signal,
            });
            signal?.throwIfAborted();
            this.dispatchEvent(
              new CustomEvent("downloadend", { detail: { id: loadingId } }),
            );
          }

          // Bytes are in OPFS at this point (either preexisting or just downloaded),
          // but no McapReader has been built around them yet for this instance.
          // Building one parses the file's header and summary, so damage to either
          // surfaces here rather than partway through iteration.
          let reader: McapReader;
          try {
            reader = await McapReader.forFile(local, {
              abortSignal: signal,
              cache: false,
            });
          } catch (err) {
            throw await this.toCacheReadError(err, signal, local);
          }
          this.cachedFileToReaderMap.set(cacheKey, reader);
          relevant.forEach((mp) => {
            remaining.delete(mp.message_path_id);
          });
          readers.push({ reader, file: local });
        },
      );
    }

    if (readers.length === 0) {
      throw new Error(`Failed to read data topic ${this.topicId}`, {
        cause: `Message paths ${Array.from(messagePathIds).join(", ")} not mapped to any representations`,
      });
    }

    if (remaining.size > 0) {
      throw new Error(
        [
          `Failed to construct a reader for Topic '${this.topicId}'.`,
          "Could not determine where to find data for the following MessagePaths:",
          JSON.stringify(Array.from(remaining)),
        ].join(" "),
      );
    }

    return readers;
  }

  private async getRepresentationAssociatedFileRecord(
    representation: RepresentationRecord,
    signal?: AbortSignal,
  ): Promise<FileRecord> {
    // Memoize per representation so a single iter call doesn't re-fetch the same
    // FileRecord once for every message path that maps to it.
    const cacheKey = representation.representation_id;
    if (this.representationFileRecordCache.has(cacheKey)) {
      return this.representationFileRecordCache.get(cacheKey)!;
    } else {
      const record = await this.fileService.getFileRecord(
        representation.association.association_id,
        { abortSignal: signal },
      );
      this.representationFileRecordCache.set(cacheKey, record);
      return record;
    }
  }

  private async isCached(file: LocalFile, expectedSize: number) {
    return await file.exists(expectedSize);
  }

  /**
   * Wrap `reader` so a failure reading its bytes evicts only its own cache entry.
   *
   * Bound rather than a method reference because it is passed to `Array.map`.
   */
  private scopeEvictionToReader = ({
    reader,
    file,
  }: CachedRepresentationReader): ReaderLike => ({
    file: reader.file,
    iterMessageChunks: (options) =>
      this.readFromCache(
        reader.iterMessageChunks(options),
        options?.abortSignal,
        file,
      ),
    iterMessageChunksIndices: (options) =>
      this.readFromCache(
        reader.iterMessageChunksIndices(options),
        options?.abortSignal,
        file,
      ),
  });

  /**
   * Forward everything `source` produces. `source` reads OPFS-cached bytes, so a
   * failure means the cache entry behind it is unusable: evict and raise a
   * {@link CacheReadError} in place of whatever the parser threw, so a retry
   * re-downloads.
   *
   * `signal` is the one `source` iterates under, and serves only to tell a cancelled
   * read apart from a failed one. `file` is the entry `source` draws on, when the
   * caller knows it; without it every entry this reader holds is evicted.
   *
   * An error that has already been converted passes through untouched, so a failure
   * attributed to one reader is not re-attributed to all of them when it surfaces
   * again through the surrounding `zipMessages`.
   */
  private async *readFromCache<T>(
    source: AsyncIterable<T>,
    signal?: AbortSignal,
    file?: LocalFile,
  ): AsyncGenerator<T, void, undefined> {
    try {
      yield* source;
    } catch (err) {
      if (isCacheReadError(err)) {
        throw err;
      }
      throw await this.toCacheReadError(err, signal, file);
    }
  }

  /**
   * Given an error raised while reading OPFS-cached bytes, invalidate this topic's
   * cache and return the {@link CacheReadError} to throw in its place.
   *
   * A cancelled read says nothing about the cached bytes, so an abort is rethrown
   * as-is and the cache is left alone. That case is recognized from `signal` as well
   * as from the error, because these reads run in a web worker and an abort forwarded
   * into a worker arrives as a bare string reason rather than a DOMException, which
   * `isAbortError` does not match (see `AbortSignalReceiver` in `@/shared/webworker`).
   */
  private async toCacheReadError(
    err: unknown,
    signal?: AbortSignal,
    file?: LocalFile,
  ): Promise<Error> {
    if (isAbortError(err) || signal?.aborted === true) {
      throw err;
    }
    await this.invalidateCache(file);
    return new CacheReadError(
      `Failed to read cached data for topic ${this.topicId}`,
      { cause: err },
    );
  }

  /**
   * Delete an OPFS-cached representation file and drop the reader built over it, so
   * the next read re-downloads from object storage.
   *
   * `only` scopes the eviction to the entry whose bytes failed. That matters because
   * `ReaderRegistry` shares one reader per key among every consumer that asks for that
   * key, so evicting everything would delete files a concurrent iteration on
   * this same instance is still reading, and it would surface a cache error on a
   * panel whose own bytes were fine. Callers that cannot attribute the failure omit
   * it and fall back to evicting every entry this reader holds.
   *
   * Deletion is best-effort. A failure leaves the entry poisoned and the next read
   * failing the same way, which is no worse than not having tried, so it is reported
   * rather than raised over the read error that prompted it.
   */
  private async invalidateCache(only?: LocalFile): Promise<void> {
    const cachedFiles =
      only === undefined ? Array.from(this.localFileCache.values()) : [only];

    for (const file of cachedFiles) {
      this.cachedFileToReaderMap.delete(file.id());
      for (const [key, cached] of this.localFileCache) {
        if (cached === file) {
          this.localFileCache.delete(key);
        }
      }
    }

    await Promise.all(
      cachedFiles.map(async (file) => {
        try {
          await file.delete();
        } catch (err) {
          LoggerService.error(
            new Error(`Failed to evict cached data at ${file.toString()}`, {
              cause: err,
            }),
          );
        }
      }),
    );
  }
}
